import { PLAYER } from "../../../../common/constants.ts";
import { helpers } from "../util/helpers.ts";
import i18n from "../util/i18n.ts";
import { useLocal } from "../util/local.ts";
import { translateTeamShortName } from "../util/teamNames.ts";
import { MobileTooltip } from "./MobileTooltip.tsx";

type Props = {
	tid: number;
	abbrev: string;
	className?: string;
	season?: number;
};

type TeamAbbrevTextProps = {
	abbrev: string;
	tid?: number;
};

const getLocalizedTeamShortName = (
	teamInfoCache: Record<
		number,
		| {
				abbrev?: string;
				name?: string;
		  }
		| undefined
	>,
	tid: number | undefined,
	abbrev: string,
) => {
	const team = tid !== undefined ? teamInfoCache[tid] : undefined;
	if (team) {
		return translateTeamShortName(team, abbrev);
	}

	const matchingTeam = Object.values(teamInfoCache).find(
		(teamInfo) => teamInfo?.abbrev === abbrev,
	);
	return translateTeamShortName(matchingTeam, abbrev);
};

export const TeamAbbrevText = ({
	abbrev,
	tid,
}: TeamAbbrevTextProps) => {
	const teamInfoCache = useLocal((state) => state.teamInfoCache);

	return <>{getLocalizedTeamShortName(teamInfoCache, tid, abbrev)}</>;
};

export const TeamAbbrevLink = ({ tid, abbrev, className, season }: Props) => {
	const teamInfoCache = useLocal((state) => state.teamInfoCache);

	if (!abbrev) {
		return null;
	}

	if (tid === PLAYER.DOES_NOT_EXIST) {
		return (
			<MobileTooltip
				title={i18n.t("teamAbbrev.doesNotExist", {
					ns: "ui",
					defaultValue: "Does Not Exist",
				})}
			>
				<span className={className}>
					{i18n.t("teamAbbrev.doesNotExistShort", {
						ns: "ui",
						defaultValue: "DNE",
					})}
				</span>
			</MobileTooltip>
		);
	}

	if (tid === PLAYER.TOT) {
		return (
			<MobileTooltip
				title={
					season !== undefined
						? i18n.t("teamAbbrev.totalForSeason", {
								ns: "ui",
								defaultValue: "Total for {{season}}",
								season,
							})
						: i18n.t("common.total", { ns: "ui", defaultValue: "Total" })
				}
			>
				<span className={className}>
					{i18n.t("teamAbbrev.totalShort", {
						ns: "ui",
						defaultValue: "TOT",
					})}
				</span>
			</MobileTooltip>
		);
	}

	let leagueUrlParam: Parameters<typeof helpers.leagueUrl>[0];

	if (tid === PLAYER.FREE_AGENT) {
		leagueUrlParam = ["free_agents"];
	} else if (tid === PLAYER.UNDRAFTED) {
		leagueUrlParam = ["draft_scouting"];
	} else if (tid < 0) {
		// Weird or retired
		return null;
	} else {
		leagueUrlParam = ["roster", `${abbrev}_${tid}`, season];
	}

	return (
		<a className={className} href={helpers.leagueUrl(leagueUrlParam)}>
			{getLocalizedTeamShortName(teamInfoCache, tid, abbrev)}
		</a>
	);
};

export const wrappedTeamAbbrevLink = ({
	tid,
	abbrev,
	className,
	season,
}: Props) => {
	let text;
	if (!abbrev) {
		text = undefined;
	} else if (tid === PLAYER.DOES_NOT_EXIST) {
		text = "DNE";
	} else if (tid === PLAYER.TOT) {
		text = "TOT";
	} else if (tid < 0) {
		text = undefined;
	} else {
		text = abbrev;
	}

	return {
		value: (
			<TeamAbbrevLink
				tid={tid}
				abbrev={abbrev}
				className={className}
				season={season}
			/>
		),
		sortValue: text,
		searchValue: text,
	};
};
